import eventbus from 'tiny-emitter/instance';

import { TYPE, RENDERER_MODE } from '@/constants';

import { DraggableMixin } from '@/core/mixins/DraggableMixin';
import { RotatableMixin } from '@/core/mixins/RotatableMixin';
import { SelectableMixin } from '@/core/mixins/SelectableMixin';
import { CategoryMixin } from '@/core/mixins/CategoryMixin';

import Model, { ModelParams } from '../model';
import SVG from 'svg.js';

export class GA extends DraggableMixin(RotatableMixin(CategoryMixin(SelectableMixin(Model)))) {
  scx: number = 0;
  scy: number = 0;
  minHOffset: number = 0;
  maxHOffset: number = 0;
  minVOffset: number = 0;
  maxVOffset: number = 0;
  label: SVG.Text | null = null;

  constructor(params: ModelParams) {
    super(params);
    this.setup();
  }

  get seatsCount() {
    if (this.category) {
      //CategoryMixin
      const category = this.app.getCategoryObj(this.categoryId);
      if (category && category.capacity) {
        this.attributes.capacity = category.capacity;
        return this.attributes.capacity;
      }
    }
    return 0;
  }

  get type() {
    return TYPE.GA;
  }

  get pos() {
    return this.attributes.pos || [];
  }

  set pos(val) {
    this.attributes.pos = val;
  }

  setup() {
    this.view = this.canvas.group().addClass(this.type);
  }

  initLabel() {
    const { label, shapecenter } = this.attributes;
    const { text, size, fill } = label;
    if (shapecenter.length === 0) {
      const { cx, cy } = this.shapeRBox();
      this.scx = cx;
      this.scy = cy;
      this.attributes.shapecenter = [cx, cy];
    } else {
      this.scx = shapecenter[0];
      this.scy = shapecenter[1];
    }
    this.label = this.canvas
      .plain(text)
      .font({ size, family: 'Roboto', style: 'normal' })
      .attr({
        fill,
        x: this.scx,
        y: this.scy,
        'text-anchor': 'middle',
        'dominant-baseline': 'central',
      })
      .addClass('label');
    this.view!.add(this.label);
    this.changeLabelTransform();
    if (this.category && !RENDERER_MODE) {
      this.applyCategoryColor(this.app.getCategoryColor(this.categoryId));
    }
  }

  get seatCanBeSelectedOrDeselected() {
    return true;
  }

  recalculateEdgeOffsets() {
    const { w, h } = this.shapeRBox();
    const halfHLen = w / 2;
    const halfVLen = h / 2;
    this.minHOffset = -halfHLen;
    this.maxHOffset = halfHLen;
    this.minVOffset = -halfVLen;
    this.maxVOffset = halfVLen;
  }

  changeLabel(value: string) {
    this.changeLabelProp('text', value);
    this.label!.plain(value);
  }

  changeHOffset(offset: number) {
    this.changeLabelProp('hOffset', offset);
    this.changeLabelTransform();
  }

  changeVOffset(offset: number) {
    this.changeLabelProp('vOffset', offset);
    this.changeLabelTransform();
  }

  changeFontSize(size: number) {
    this.changeLabelProp('size', size);
    this.label!.font({ size });
  }

  changeLabelStroke(color: string) {
    if (!color) return;
    this.changeLabelProp('stroke', color);
    this.changeLabelProp('fill', color);
    this.label!.style({ fill: color, stroke: color });
  }

  changeRotation(rotation: number) {
    this.changeLabelProp('rotation', rotation);
    this.changeLabelTransform();
  }

  changeLabelTransform() {
    const { hOffset, vOffset, rotation } = this.attributes.label;
    this.label!.attr({
      transform: `translate(${hOffset}, ${vOffset}) rotate(${rotation}, ${this.scx}, ${this.scy})`,
    });
  }

  changeLabelProp(name: string, value: any) {
    if (value === this.attributes.label[name]) return;
    this.attributes.label[name] = value;
  }

  shapeRBox(parent = this.svg) {
    return this.shape!.rbox(parent);
  }

  destroyView() {
    this.view!.remove();
  }

  destroy() {
    this.destroyView();
    eventbus.emit('unit:removed', this);
  }

  assignCategory(categoryId: number) {
    this.categoryId = categoryId;
  }

  unassignCategory() {
    this.categoryId = null;
  }

  onScaleChange(scale: number) {
    this.view!.transform({ scale }, true);
  }

  changeMeta1(value: string) {
    this.setAttribute('meta1', value);
  }

  changeMeta2(value: string) {
    this.setAttribute('meta2', value);
  }
}
